/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';

import { isElementNull } from '../../../../utils';

import {
  HierarchyTreeComponent,
  IFlatTreeNode,
  IHierarchyTreeData,
  IHierarchyTreeNode,
  IHierarchyTreeNodeRightClickData
} from '../../../core/components';
import { EMapHierarchyMaxSelectionType, EVehicleSelectionMode, MapHierarchyInfo } from '../../../core/models';
import { ECheckboxState } from '../../../core/models/checkbox-state.enum';
import { IVehicleTreeNodeRightClickData } from '../../models';

@Component({
  selector: 'fm-vehicle-selection-panel',
  templateUrl: './vehicle-selection-panel.component.html',
  styleUrls: ['./vehicle-selection-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VehicleSelectionPanelComponent implements OnChanges {
  @Input()
  public selectionMode: EVehicleSelectionMode;

  @Input()
  public set maxPlottableVehicles(value: number) {
    this.maxSelectionMap = new Map<number, number>();
    this.maxSelectionMap.set(EMapHierarchyMaxSelectionType.Vehicles, value);
  }
  @Input()
  public set vehicleListScopingTreeData(value: IFlatTreeNode<string>[]) {
    this._vehicleListScopingTreeData = value;
    this.vehicleListScopingTotalNodesCount = this._vehicleListScopingTreeData.length;
    this.vehicleListScopingSelectedNodesCount = this._vehicleListScopingTreeData.filter(
      node => node.state === ECheckboxState.Checked
    ).length;
  }
  public get vehicleListScopingTreeData(): IFlatTreeNode<string>[] {
    return this._vehicleListScopingTreeData;
  }
  @Input()
  public vehicleListTreeData: IHierarchyTreeData<string>;
  @Input()
  public isVehicleScopingEnabled: boolean;
  @Input()
  public isAssetsEnabled: boolean;
  @Input()
  public anyPoweredAssets: boolean;
  @Input()
  public anyNonPoweredAssets: boolean;

  @Output()
  public editScopingClick = new EventEmitter<TemplateRef<any>>();
  @Output()
  public selectionChange = new EventEmitter<Map<string, ECheckboxState>>();
  @Output()
  public selectionModeChange = new EventEmitter<EVehicleSelectionMode>();
  @Output()
  public vehicleClick = new EventEmitter<string>();
  @Output()
  public vehicleRightClick = new EventEmitter<IVehicleTreeNodeRightClickData>();
  @Output()
  public vehicleListScopingClose = new EventEmitter<void>();
  @Output()
  public vehicleListScopingSave = new EventEmitter<string[]>();

  @ViewChild(HierarchyTreeComponent)
  public hierarchyTree: HierarchyTreeComponent;

  @HostBinding('class.selection-panel-ready')
  isSelectionPanelReady = false;

  public EVehicleSelectionMode = EVehicleSelectionMode;
  public maxSelectionMap: Map<number, number>;
  public vehicleListScopingSelectedNodesCount: number;
  public vehicleListScopingTotalNodesCount: number;

  private _vehicleListScopingTreeData: IFlatTreeNode<string>[];
  private readonly _defaultRootLabel = 'Entire Fleet';

  constructor(@Inject(DOCUMENT) private readonly _document: Document) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectionMode'] || changes['vehicleListTreeData']) {
      this._changeRootLevelLabel();
    }
  }

  public onViewportChange(): void {
    this.hierarchyTree.virtualScroll.checkViewportSize();
  }

  public onEditScopingClick(modalTemplate: TemplateRef<any>): void {
    this.editScopingClick.emit(modalTemplate);
  }

  public onSelectionModeChange(mode: EVehicleSelectionMode): void {
    this.selectionModeChange.emit(mode);
  }

  public onVehicleListScopingClose(): void {
    this.vehicleListScopingClose.emit();
  }

  public onVehicleListScopingSave(selectedGroups: string[]): void {
    this.vehicleListScopingSave.emit(selectedGroups);
  }

  public onVehiclesSelectionChange(nodes: IHierarchyTreeNode<string>[]): void {
    const selection = new Map<string, ECheckboxState>();
    nodes.forEach(node => {
      selection.set(node.id, node.state);
    });
    this.selectionChange.emit(selection);
  }

  public onHierarchyItemClick(treeNode: IHierarchyTreeNode<string>): void {
    if (!treeNode.isExpandable) {
      this.vehicleClick.emit(treeNode.id);
    }
  }

  public onVehicleRightClick(rightClickData: IHierarchyTreeNodeRightClickData<string>): void {
    if (!rightClickData.treeNode.isExpandable && rightClickData.treeNode.state === ECheckboxState.Checked) {
      // TODO: investigate a better way to handle this
      const mapElement = <HTMLElement>this._document.querySelector('div.fm-map-container');
      const clickPosition = new google.maps.Point(
        rightClickData.eventPageX,
        rightClickData.eventPageY - mapElement.getBoundingClientRect().top
      );
      const vehicleRightClickData: IVehicleTreeNodeRightClickData = {
        clickPosition,
        vehicleId: rightClickData.treeNode.id
      };
      this.vehicleRightClick.emit(vehicleRightClickData);
    }
  }

  public onDeselectAll(): void {
    this.selectionChange.emit(new Map());
  }

  private _changeRootLevelLabel(): void {
    const rootElement = this.vehicleListTreeData?.data?.find(item => item.level === 0);

    if (isElementNull(rootElement)) {
      return;
    }

    if (rootElement) {
      if (this.selectionMode === EVehicleSelectionMode.Groups && rootElement.name !== this._defaultRootLabel) {
        return;
      }

      rootElement.name = MapHierarchyInfo.rootGroupLocalized.get(this.selectionMode);

      this.isSelectionPanelReady = true;
    }
  }
}
